// Display formats stored per dimension/measure on ReportBuilderOptions. Mirrors the .NET
// AgentBuilderFieldFormatter so the screen and the Excel export render values the same way.
// Fields without a stored format fall back to resolveDefaultFieldFormat (today's behaviour).

export type FieldFormatKind = 'currency' | 'percent' | 'number' | 'integer' | 'date' | 'datetime' | 'text'

export type FieldFormat = {
  kind: FieldFormatKind
  decimals?: number | null
  currencySymbol?: string | null
}

/** Sent on save to clear a stored format back to the default. */
export const AUTO_FIELD_FORMAT_KIND = 'auto'

export const DEFAULT_FIELD_FORMAT_DECIMALS = 2
export const MAX_FIELD_FORMAT_DECIMALS = 6
const DEFAULT_CURRENCY_SYMBOL = '$'

export const FIELD_FORMAT_KIND_LABELS: Record<FieldFormatKind, string> = {
  currency: 'Currency',
  percent: 'Percent',
  number: 'Number',
  integer: 'Whole number',
  date: 'Date',
  datetime: 'Date & time',
  text: 'Text',
}

const NUMERIC_KINDS: FieldFormatKind[] = ['currency', 'percent', 'number', 'integer']
const DATE_KINDS: FieldFormatKind[] = ['date', 'datetime']
const ALL_KINDS: FieldFormatKind[] = [...NUMERIC_KINDS, ...DATE_KINDS, 'text']

// Ported from the original vizUtils name heuristic; kept in sync with AgentBuilderFieldFormatter.cs.
const CURRENCY_TOKENS = ['amount', 'ar', 'balance', 'billing', 'billings', 'cash', 'cost', 'debtors', 'fees', 'income', 'invoice', 'margin$', 'net profit', 'profit', 'revenue', 'sales', 'wip', 'write']
const PERCENT_TOKENS = ['%', 'margin', 'percent', 'percentage', 'productivity', 'rate', 'recoverability', 'recovery', 'utilisation', 'utilization']

export function isNumericFormatKind(kind: string | null | undefined): kind is FieldFormatKind {
  return NUMERIC_KINDS.includes(kind as FieldFormatKind)
}

export function isDateFormatKind(kind: string | null | undefined): kind is FieldFormatKind {
  return DATE_KINDS.includes(kind as FieldFormatKind)
}

export function formatUsesDecimals(kind: string | null | undefined): boolean {
  return kind === 'currency' || kind === 'percent' || kind === 'number'
}

/** Format kinds an admin may pick for a field of the given ReportBuilderOptions type. */
export function formatKindsForFieldType(fieldType: string | null | undefined): FieldFormatKind[] {
  const type = String(fieldType ?? '').trim().toLowerCase()
  if (type === 'number') return [...NUMERIC_KINDS, 'text']
  if (type === 'date') return [...DATE_KINDS, 'text']
  return ['text']
}

export function isFormatKindAllowedForType(kind: string | null | undefined, fieldType: string | null | undefined): boolean {
  return formatKindsForFieldType(fieldType).includes(kind as FieldFormatKind)
}

/**
 * Reads a format from API/import JSON (camelCase or PascalCase). Returns null when missing, "auto",
 * unknown, or not valid for the field type, so callers fall back to the default.
 */
export function normalizeFieldFormat(raw: unknown, fieldType?: string | null): FieldFormat | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const record = raw as Record<string, unknown>
  const kind = String(record['kind'] ?? record['Kind'] ?? '').trim().toLowerCase()
  if (!ALL_KINDS.includes(kind as FieldFormatKind)) return null
  if (fieldType !== undefined && fieldType !== null && !isFormatKindAllowedForType(kind, fieldType)) return null

  const format: FieldFormat = { kind: kind as FieldFormatKind }
  const rawDecimals = record['decimals'] ?? record['Decimals']
  const decimals = Number(rawDecimals)
  if (formatUsesDecimals(kind) && rawDecimals !== undefined && rawDecimals !== null && rawDecimals !== '' && Number.isFinite(decimals)) {
    format.decimals = Math.min(MAX_FIELD_FORMAT_DECIMALS, Math.max(0, Math.trunc(decimals)))
  }
  const symbol = String(record['currencySymbol'] ?? record['CurrencySymbol'] ?? '').trim()
  if (kind === 'currency' && symbol) format.currencySymbol = symbol
  return format
}

export function normalizeFieldFormatRecord(raw: unknown): Record<string, FieldFormat> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const formats: Record<string, FieldFormat> = {}
  Object.entries(raw as Record<string, unknown>).forEach(([column, value]) => {
    const format = normalizeFieldFormat(value)
    if (format) formats[column] = format
  })
  return formats
}

function normalizeMetricName(value: string) {
  return String(value ?? '').toLowerCase().replace(/[_-]+/g, ' ').trim()
}

export function resolveMeasureKindFromName(name: string | null | undefined): 'currency' | 'percent' | 'number' {
  const normalized = normalizeMetricName(name ?? '')
  if (normalized.includes('$')) return 'currency'
  if (PERCENT_TOKENS.some((token) => normalized.includes(token))) return 'percent'
  if (CURRENCY_TOKENS.some((token) => normalized.includes(token))) return 'currency'
  return 'number'
}

/** The compatibility default used when a field has no stored format. */
export function resolveDefaultFieldFormat(name: string | null | undefined, fieldType: string | null | undefined, isMeasure: boolean): FieldFormat {
  const type = String(fieldType ?? '').trim().toLowerCase()
  if (type === 'number') return isMeasure ? { kind: resolveMeasureKindFromName(name) } : { kind: 'text' }
  if (type === 'date') return { kind: 'date' }
  return { kind: 'text' }
}

export type FieldFormatCatalogOption = {
  name: string
  value: string
  type: string
  format?: FieldFormat | null
}

export type FieldFormatCatalog = {
  dimensions?: FieldFormatCatalogOption[]
  measures?: FieldFormatCatalogOption[]
}

function normalizeToken(value: string | null | undefined) {
  return String(value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '')
}

function bracketLabel(value: string | null | undefined): string {
  const text = String(value ?? '')
  return text.match(/\[([^\]]+)\]/)?.[1] ?? text
}

function findCatalogField(column: string, catalog: FieldFormatCatalog): { option: FieldFormatCatalogOption; isMeasure: boolean } | null {
  const measures = catalog.measures ?? []
  const dimensions = catalog.dimensions ?? []
  const trimmed = column.trim().toLowerCase()
  const exactMeasure = measures.find((option) => option.name?.trim().toLowerCase() === trimmed)
  if (exactMeasure) return { option: exactMeasure, isMeasure: true }
  const exactDimension = dimensions.find((option) => option.name?.trim().toLowerCase() === trimmed)
  if (exactDimension) return { option: exactDimension, isMeasure: false }

  const token = normalizeToken(column)
  if (!token) return null
  const keys: Array<(option: FieldFormatCatalogOption) => string> = [
    (option) => option.name,
    (option) => option.value,
    (option) => bracketLabel(option.value),
  ]
  for (const key of keys) {
    const measure = measures.find((option) => normalizeToken(key(option)) === token)
    if (measure) return { option: measure, isMeasure: true }
    const dimension = dimensions.find((option) => normalizeToken(key(option)) === token)
    if (dimension) return { option: dimension, isMeasure: false }
  }

  const label = bracketLabel(column)
  return label !== column ? findCatalogField(label, catalog) : null
}

/**
 * Matches a display name or DAX reference ("[Revenue]", "Revenue") to a columnFormats key ("'Sales'[Revenue]")
 * the way the server's FindField does. Only an unambiguous match is used, so "Revenue" never binds to "Revenue LY".
 */
function findUniqueColumnFormat(column: string, columnFormats: Record<string, FieldFormat> | null | undefined): FieldFormat | null {
  const entries = Object.entries(columnFormats ?? {})
  if (entries.length === 0) return null
  const keyFns: Array<(value: string) => string> = [
    (value) => normalizeToken(value),
    (value) => normalizeToken(bracketLabel(value)),
  ]
  for (const keyFn of keyFns) {
    const token = keyFn(column)
    if (!token) continue
    const matches = entries.filter(([key]) => keyFn(key) === token)
    if (matches.length === 1) return matches[0]?.[1] ?? null
    if (matches.length > 1) return null
  }
  return null
}

/**
 * Resolves a column's format: server-resolved columnFormats first, then the catalog's stored format,
 * then the default. Returns null when nothing is known about the column.
 */
export function resolveFieldFormat(
  column: string | null | undefined,
  options: { columnFormats?: Record<string, FieldFormat> | null; catalog?: FieldFormatCatalog | null } = {},
): FieldFormat | null {
  if (!column) return null
  const fromServer = options.columnFormats?.[column]
    ?? Object.entries(options.columnFormats ?? {}).find(([key]) => key.toLowerCase() === column.toLowerCase())?.[1]
  if (fromServer) return fromServer
  const fuzzy = findUniqueColumnFormat(column, options.columnFormats)
  if (fuzzy) return fuzzy
  if (!options.catalog) return null
  const match = findCatalogField(column, options.catalog)
  if (!match) return null
  return normalizeFieldFormat(match.option.format, match.option.type)
    ?? resolveDefaultFieldFormat(match.option.name, match.option.type, match.isMeasure)
}

/** Half-up rounding that agrees with Excel's display rounding (1.995 -> 2.00, 0.005 -> 0.01). */
export function roundHalfUp(value: number, decimals: number): number {
  const factor = 10 ** decimals
  const abs = Number(Math.abs(value).toPrecision(15))
  const scaled = Number((abs * factor).toPrecision(15))
  const rounded = Math.round(scaled) / factor
  return rounded === 0 ? 0 : Math.sign(value) * rounded
}

function resolveDecimals(format: FieldFormat): number {
  if (format.kind === 'integer') return 0
  if (!formatUsesDecimals(format.kind)) return 0
  const decimals = format.decimals ?? DEFAULT_FIELD_FORMAT_DECIMALS
  return Math.min(MAX_FIELD_FORMAT_DECIMALS, Math.max(0, decimals))
}

function parseNumeric(value: string | number): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  const text = value.trim()
  if (!text) return null
  const parsed = Number(text.replace(/[$,\s]/g, ''))
  return Number.isFinite(parsed) ? parsed : null
}

function formatFixed(value: number, decimals: number) {
  return new Intl.NumberFormat('en-AU', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(value)
}

const ISO_DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/
const ISO_DATE_TIME = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/

function parseDateValue(value: string | number): Date | null {
  if (typeof value === 'number') {
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : date
  }
  const text = value.trim()
  // Parse ISO parts directly so a date-only value is never shifted by the viewer's timezone.
  const dateOnly = text.match(ISO_DATE_ONLY)
  if (dateOnly) return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
  const dateTime = text.match(ISO_DATE_TIME)
  if (dateTime) {
    return new Date(Number(dateTime[1]), Number(dateTime[2]) - 1, Number(dateTime[3]), Number(dateTime[4]), Number(dateTime[5]))
  }
  const parsed = new Date(text)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

function pad(value: number) {
  return String(value).padStart(2, '0')
}

/**
 * Formats a value for display. Numbers use 2 decimals by default, percentages are fractions
 * (0.4525 -> 45.25%), and negatives keep the minus sign (Excel shows parentheses instead).
 * `compact` drops decimals for chart axis ticks.
 */
export function formatFieldValue(
  value: string | number | null | undefined,
  format: FieldFormat | null | undefined,
  options: { compact?: boolean } = {},
): string {
  if (value === null || value === undefined || value === '') return '-'
  if (!format || format.kind === 'text') return String(value)

  if (isDateFormatKind(format.kind)) {
    const date = parseDateValue(value)
    if (!date) return String(value)
    const day = `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${pad(date.getFullYear() % 100)}`
    return format.kind === 'datetime' ? `${day} ${pad(date.getHours())}:${pad(date.getMinutes())}` : day
  }

  const numeric = parseNumeric(value)
  if (numeric === null) return String(value)
  const decimals = options.compact ? 0 : resolveDecimals(format)

  if (format.kind === 'percent') {
    const percent = roundHalfUp(numeric * 100, decimals)
    return `${formatFixed(percent, decimals)}%`
  }

  const rounded = roundHalfUp(numeric, decimals)
  if (format.kind === 'currency') {
    const symbol = format.currencySymbol || DEFAULT_CURRENCY_SYMBOL
    return `${rounded < 0 ? '-' : ''}${symbol}${formatFixed(Math.abs(rounded), decimals)}`
  }
  return formatFixed(rounded, decimals)
}

/** Insight-prompt value format key for a resolved format ("currency" | "percent" | "number"), or null for non-numeric kinds. */
export function toInsightValueFormat(format: FieldFormat | null | undefined): 'currency' | 'percent' | 'number' | null {
  if (!format) return null
  if (format.kind === 'currency' || format.kind === 'percent') return format.kind
  if (format.kind === 'number' || format.kind === 'integer') return 'number'
  return null
}
